/**
 * Development and Beta Mode Utilities
 * Provides safe development environment detection and beta feature gating
 */

export const isProd = process.env.NODE_ENV === 'production';

/**
 * Check if Beta/Dev mode is enabled
 * Enabled in non-production when:
 * - VITE_NURA_BETA=1 environment variable is set
 * - ?dev=1 query parameter is present
 */
export const isDevBetaEnabled = (): boolean => {
  // Hard guard: never allow in production (double check)
  if (process.env.NODE_ENV === 'production' || isProd) {
    return false;
  }
  
  // Check environment variable
  if (process.env.VITE_NURA_BETA === '1') {
    return true;
  }
  
  // Check URL query parameter or localStorage
  if (typeof window !== 'undefined') {
    return window.location.search.includes('dev=1') ||
           localStorage.getItem('nura:beta') === '1';
  }
  
  return false;
};

/**
 * Development environment info for debugging
 */
export const getDevInfo = () => ({
  isProd,
  isDevBetaEnabled: isDevBetaEnabled(),
  env: process.env.NODE_ENV,
  viteNuraBeta: process.env.VITE_NURA_BETA,
  urlHasDevParam: typeof window !== 'undefined' ? window.location.search.includes('dev=1') : false
});

/**
 * Explicit demo mode enablement for UI toggles
 * True only when the user opts in via `?demo=1` or localStorage flag `nura:demo=1`.
 * Does not implicitly enable in prod; safe no-op on server.
 */
export const isDemoModeEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const hasQuery = window.location.search.includes('demo=1');
    const flag = localStorage.getItem('nura:demo') === '1';
    return hasQuery || flag;
  } catch {
    return false;
  }
};